import { useMutation } from "@apollo/client";
import { createAgendaHonor, updateAgendaShift } from "./queries";
import { useAuthContext } from "../../../contexts/AuthContext";
import { AgendaHonorType, AgendaShift, AgendaUser, CreateAgendaHonorMutation, CreateAgendaHonorMutationVariables, UpdateAgendaShiftMutation, UpdateAgendaShiftMutationVariables } from "../../../graphql/API";
import TopicNotificationService from "../TopicService";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useDateService from "../DateService";
import useHelperService from "../HelperService";

const useShiftService = (
  shift: AgendaShift, 
  manager: boolean = false, 
  locale: string = "en", 
  givenDate?: string, 
  nav?: string, 
  managers?: AgendaUser[]) => {
    // AUTH
    const {userId} = useAuthContext();
    // I18N
    const { t } = useTranslation();
    // NAVIGATION
    const navigate = useNavigate();
    // SERVICES
    const {sendTopicNotification} = TopicNotificationService();
    // DATE SERVICES
    const {getTIMEZONE, isNowGreater} = useDateService();
    // SERVICE HELPERS
    const {generateCustomId} = useHelperService();
    // TIMEZONE SETTINGS
    const TIMEZONE = getTIMEZONE();
    // DB QUERIES
    const [doUpdateShift] = useMutation<UpdateAgendaShiftMutation, UpdateAgendaShiftMutationVariables>(updateAgendaShift);
    const [doCreateHonor] = useMutation<CreateAgendaHonorMutation, CreateAgendaHonorMutationVariables>(createAgendaHonor);
    // FUNCTIONS
    const onUpdateShiftAccept = async () => {
      try {
          await doUpdateShift({
            variables: {
              input: {
                shiftId: shift.shiftId,
                accepted: true,
              },
            },
          });
          // if (userId !== AuthUserId) {
          //   if (confirmed) {
          //     sendTopicNotification(role ?? undefined, location ?? undefined, user.FCMtoken ?? undefined, userId ?? undefined, shiftId, startDate, endDate, "confirmShift");
          //   } else {
          //     sendTopicNotification(role ?? undefined, location ?? undefined, user.FCMtoken ?? undefined, userId ?? undefined, shiftId, startDate, endDate, "shift");
          //   }
          // }
          const alreadyDone = isNowGreater(new Date(shift.stopAt), TIMEZONE);
          if (!alreadyDone) {
            managers?.forEach(manager => {
              // if (manager?.FCMtoken) {
                if (manager?.userId != userId) {
                  // console.log(manager.username);
                    sendTopicNotification(shift.role ?? undefined, shift.location ?? undefined, manager?.FCMtoken ?? undefined, manager.userId ?? undefined, shift.shiftId ?? undefined, shift.startAt, shift.stopAt, "shiftAccepted");
                }
              // }
            });
          }
          if (nav === "ShiftDetail") {
            navigate(`${t('link.shift')}/${shift.shiftId}`, { replace: true, state: { refetchData: true } });
          } else {
            navigate(t('link.shifts'), { replace: true, state: { refetchData: true } });
          }
      } catch(error) {
          alert(t('shift.errorAcceptingShift') + ": " + (error as Error).message);
      };
    };

    const onUpdateShiftConfirm = async () => {
      try {
          await doUpdateShift({
            variables: {
              input: {
                shiftId: shift.shiftId,
                confirmed: true,
              },
            },
          });
          const alreadyDone = isNowGreater(new Date(shift.stopAt), TIMEZONE);
          if (!alreadyDone) {
            if (userId !== shift.userId) {
              sendTopicNotification(shift.role ?? undefined, shift.location ?? undefined, undefined, shift.userId ?? undefined, shift.shiftId, shift.startAt, shift.stopAt, "confirmShift");   
            }
          }
      
          if (nav === "ShiftDetail") {
            navigate(`${t('link.shift')}/${shift.shiftId}`, { replace: true, state: { refetchData: true } });
          } else if (nav === "AgendaManagement"){
            navigate(`/agenda/${t('link.management')}`, { replace: true, state: { refetchData: true } });
          } else {
            navigate(t('link.scheduler'), { replace: true, state: { refetchData: true } });
          }
      } catch(error) {
          alert(t('shift.errorConfirmedShift') + ": " + (error as Error).message);
      };
    };

    const onUpdateShiftUnConfirm = async () => {
      try {
          await doUpdateShift({
            variables: {
              input: {
                shiftId: shift.shiftId,
                confirmed: false,
              },
            },
          });
          const alreadyDone = isNowGreater(new Date(shift.stopAt), TIMEZONE);
          if (!alreadyDone) {
            if (userId !== shift.userId) {
              sendTopicNotification(shift.role ?? undefined, shift.location ?? undefined, undefined, shift.userId ?? undefined, shift.shiftId ?? undefined, shift.startAt, shift.stopAt, "shift");
            }
          }
          
          if (nav === "ShiftDetail") {
            navigate(`${t('link.shift')}/${shift.shiftId}`, { replace: true, state: { refetchData: true } });
          } else if (nav === "AgendaManagement"){
            navigate(`/agenda/${t('link.management')}`, { replace: true, state: { refetchData: true } });
          } else {
            navigate(t('link.scheduler'), { replace: true, state: { refetchData: true } });
          }
      } catch(error) {
          alert(t('shift.errorUnConfirmedShift') + ": " + (error as Error).message);
      };
    };

    const onDeleteShift = async () => {
      try {
        const response = await doUpdateShift({variables: {input: {
          shiftId: shift.shiftId,
          deleted: true,
        }}});
        // navigation.navigate('AgendaManager', {refetchShift: true});
        if (nav === "ShiftDetail") {
          navigate(`${t('link.shift')}/${shift.shiftId}`, { replace: true, state: { refetchData: true } });
        } else if (nav === "AgendaManagement"){
          navigate(`/agenda/${t('link.management')}`, { replace: true, state: { refetchData: true } });
        } else {
          navigate(t('link.scheduler'), { replace: true, state: { refetchData: true } });
        }
      } catch(error) {
        alert(t('shift.errorDeletedShift') + ": " +  (error as Error).message);
      }
    }

    const onUpdateRank = async (rankData: {quality: string, performance: string, punctuality: string, rate: string}) => {
      try {
        const response = await doUpdateShift({variables: {input: {
          shiftId: shift.shiftId,
          quality: rankData.quality,
          performance: rankData.performance,
          punctuality: rankData.punctuality,
          rate: rankData.rate,
        }}});
        // console.log(response);
        // if (nav === "ShiftDetail") {
          navigate(`${t('link.shift')}/${shift.shiftId}`, { replace: true, state: { refetchData: true } });
        // } else {
        //   navigation.navigate('AgendaManager', {refetchShift: true});
        // }
      } catch(error) {
        alert(t('shift.errorDeletedShift') + ": " + (error as Error).message);
      }
    }

    const onUpdateHonor = async (honorData: string) => {
      if (honorData == "A" || honorData == "B" || honorData == "C" || honorData == "D") {
        try {
          const honorId = generateCustomId("Honor");
          const response = await doCreateHonor({variables: {input: {
            honorId: honorId,
            shiftId: shift.shiftId,
            userId: shift.userId ?? "",
            type: honorData as AgendaHonorType,
            deleted: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          }}});
          // if (nav === "ShiftDetail") {
            navigate(`${t('link.shift')}/${shift.shiftId}`, { replace: true, state: { refetchData: true } });
          // }
        } catch(error) {
          alert(t('shift.errorHonorShift') + ": " + (error as Error).message);
        }
      } else {
          alert(t('shift.errorHonorType') + ": " + t('shift.errorHonorTypeMessage'));
      }
    }

    return {
      onUpdateShiftAccept, onUpdateShiftConfirm, onUpdateShiftUnConfirm, 
      onDeleteShift, onUpdateRank, onUpdateHonor
    };
};

export default useShiftService;